/**
 * Inventory effects — the reversible footprint each transaction leaves on
 * inventory.
 *
 * Every sale / trade / rip / grading records an `Inv Effect` (a list of these
 * ops) describing exactly what it did to lots and bulk pools. Deleting or
 * editing a transaction reverses that footprint precisely, instead of guessing
 * from current state — so quantities and basis stay correct no matter what
 * happened in between (Approach 2, agreed with the user 2026-05-19).
 *
 * This module is the foundation: op types, the reversal engine, and the
 * orphan guard. Wiring into the individual save flows happens in later phases
 * (5b–5e); nothing here changes behavior on its own.
 */
import { DB } from "./db";
import { addToBulkPool, type BulkClass } from "./bulk";
import type { TableKey } from "./types";

/** A single reversible inventory mutation. */
export type InvOp =
  // Changed an existing lot's quantity (and FMV) by a signed delta. Reverse =
  // apply the inverse. Total Basis is recomputed from Unit Basis × Qty so it
  // self-heals if Unit Basis was edited in between. Status flip is restored
  // only if the lot still shows the status this op set.
  | { kind: "lotDelta"; table: "cards" | "sealed"; lotId: string;
      dQty: number; dFMV: number; statusBefore?: string; statusAfter?: string }
  // Created a brand-new lot (trade incoming, rip pull). Reverse = delete it,
  // but only after the orphan guard confirms nothing downstream uses it.
  | { kind: "lotCreate"; table: "cards" | "sealed"; lotId: string }
  // Changed a bulk pool by a signed delta. Reverse = negate.
  | { kind: "poolDelta"; klass: BulkClass; dQty: number }
  // Overwrote specific fields on a lot (grading: status / grade / class / FMV /
  // basis). `before` holds the prior values so reverse can restore them.
  | { kind: "lotFieldChange"; table: "cards" | "sealed"; lotId: string;
      before: Record<string, any> };

export type InvEffect = InvOp[];

export interface ReverseResult {
  ok: boolean;
  /** When ok is false, a human-readable reason naming the blocking record. */
  blockedBy?: string;
}

/* ---------- internal helpers ---------- */

function pkField(table: "cards" | "sealed"): string {
  return table === "cards" ? "Card Lot ID" : "Lot ID";
}

function findLot(table: "cards" | "sealed", lotId: string): Record<string, any> | null {
  const pk = pkField(table);
  return DB.table(table).find(r => r[pk] === lotId) || null;
}

/* ---------- orphan guard ---------- */

/**
 * Returns the ID of a transaction that has consumed `lotId` downstream, or
 * null if nothing has. Used before deleting a lot a transaction created — we
 * refuse rather than orphan a later sale/trade/grading/rip.
 *
 * `excludeId` skips the transaction being reversed itself (so a trade's own
 * incoming lot doesn't count as "consumed by this trade").
 */
export function lotConsumedBy(lotId: string, excludeId?: string): string | null {
  if (!lotId) return null;
  for (const s of DB.table("sales")) {
    if (s["Sale ID"] === excludeId) continue;
    if (String(s["Status"] || "Completed") !== "Completed") continue;
    const items = Array.isArray(s.Items) ? s.Items : [];
    if (items.some((it: any) => it["Lot ID"] === lotId)) return `sale ${s["Sale ID"]}`;
  }
  for (const t of DB.table("trades")) {
    if (t["Trade ID"] === excludeId) continue;
    if (String(t["Status"] || "Accepted") !== "Accepted") continue;
    const out = Array.isArray(t.Outgoing) ? t.Outgoing : [];
    if (out.some((it: any) => it["Lot ID"] === lotId)) return `trade ${t["Trade ID"]}`;
  }
  // A grading submission only DEPENDS on this record if it's still active or
  // came later (its baseline consumed our output). PRIOR, completed
  // submissions — e.g. a repair before a regrade — are history, not
  // dependents; blocking on them made multi-submission cards permanently
  // uneditable (found live 2026-07-28: Returned repair GR-26-0001 blocked
  // every edit of grading GR-26-0004 on the same card).
  const excludedSub = excludeId ? DB.table("grading").find(r => r["Submission ID"] === excludeId) : null;
  const myDateSent = excludedSub ? String(excludedSub["Date Sent"] || "") : "";
  for (const g of DB.table("grading")) {
    if (g["Submission ID"] === excludeId) continue;
    const cards = Array.isArray(g.Cards) ? g.Cards : [];
    if (!cards.some((c: any) => c["Card Lot ID"] === lotId)) continue;
    const active = String(g["Status"] || "") === "At Service";
    const theirDateSent = String(g["Date Sent"] || "");
    // Conservative when dates are missing or the excluded id isn't a grading
    // submission (lot-origin deletes still block on ANY referencing submission).
    const laterOrUnknown = !myDateSent || !theirDateSent || theirDateSent >= myDateSent;
    if (active || laterOrUnknown) return `grading ${g["Submission ID"]}`;
  }
  for (const r of DB.table("rips")) {
    if (r["Rip ID"] === excludeId) continue;
    if (r["Sealed Lot ID"] === lotId) return `rip ${r["Rip ID"]}`;
  }
  return null;
}

/* ---------- reversal engine ---------- */

/**
 * Reverse a stored effect. Two passes: first an orphan-guard check across all
 * lotCreate ops (abort entirely if any created lot is now consumed), then the
 * inverse mutations. Returns { ok:false, blockedBy } without touching anything
 * if the guard trips.
 */
export function reverseEffect(ops: InvEffect, opts?: { excludeId?: string }): ReverseResult {
  const exclude = opts?.excludeId;

  // Guard pass — don't mutate anything if reversing would orphan a downstream
  // record. lotCreate (delete the lot) and lotFieldChange (restore a graded
  // card to its pre-grading state) both become unsafe once the lot has been
  // consumed by a later sale/trade/grading.
  for (const op of ops) {
    if (op.kind === "lotCreate" || op.kind === "lotFieldChange") {
      const blocker = lotConsumedBy(op.lotId, exclude);
      if (blocker) {
        return { ok: false, blockedBy: `lot ${op.lotId} is used by ${blocker}` };
      }
    }
  }

  // Apply pass — inverse of each op.
  for (const op of ops) {
    if (op.kind === "lotDelta") {
      const lot = findLot(op.table, op.lotId);
      if (!lot) continue; // lot deleted since; nothing to restore
      const newQty = (Number(lot["Qty"]) || 0) - op.dQty;
      const unitBasis = Number(lot["Unit Basis"]) || 0;
      const patch: Record<string, any> = {
        "Qty": newQty,
        "Total Basis": +(unitBasis * newQty).toFixed(2),
        "Current FMV": +((Number(lot["Current FMV"]) || 0) - op.dFMV).toFixed(2),
      };
      if (op.statusBefore && (!op.statusAfter || lot["Status"] === op.statusAfter)) {
        patch["Status"] = op.statusBefore;
      }
      DB.update(op.table, pkField(op.table), op.lotId, patch);
    } else if (op.kind === "poolDelta") {
      addToBulkPool(op.klass, -op.dQty);
    } else if (op.kind === "lotCreate") {
      DB.remove(op.table, pkField(op.table), op.lotId);
    } else if (op.kind === "lotFieldChange") {
      const lot = findLot(op.table, op.lotId);
      if (!lot) continue;
      DB.update(op.table, pkField(op.table), op.lotId, op.before);
    }
  }
  return { ok: true };
}


/* ---------- per-transaction effect resolution ---------- */

/**
 * Reconstruct a sale's inventory effect from its own item data, for sales
 * saved before effects were recorded. Quantities and pool deltas reconstruct
 * exactly; FMV deltas are treated as 0 (FMV is refreshable, non-authoritative).
 */
export function reconstructSaleEffect(sale: any): InvEffect {
  const items: any[] = Array.isArray(sale?.Items) ? sale.Items : [];
  const effect: InvEffect = [];
  for (const it of items) {
    const qty = Number(it.Qty) || 1;
    if (it.Type === "Bulk" && it.BulkClass) {
      effect.push({ kind: "poolDelta", klass: it.BulkClass, dQty: -qty });
      continue;
    }
    if (!it["Lot ID"]) continue;
    const isSealed = !!DB.table("sealed").find(r => r["Lot ID"] === it["Lot ID"]);
    if (isSealed) {
      effect.push({ kind: "lotDelta", table: "sealed", lotId: it["Lot ID"], dQty: -qty, dFMV: 0 });
    } else {
      effect.push({ kind: "lotDelta", table: "cards", lotId: it["Lot ID"], dQty: -qty, dFMV: 0, statusBefore: "Available", statusAfter: "Sold" });
    }
  }
  return effect;
}

/**
 * Reconstruct a trade's inventory effect from its own data, for trades saved
 * before effects were recorded. Outgoing items become lotDelta/poolDelta
 * reversals; incoming items become lotCreate (their resulting Lot ID is on the
 * stored incoming row). FMV deltas are 0 (refreshable).
 */
export function reconstructTradeEffect(trade: any): InvEffect {
  const effect: InvEffect = [];
  const out: any[] = Array.isArray(trade?.Outgoing) ? trade.Outgoing : [];
  for (const o of out) {
    const qty = Number(o.Qty) || 1;
    if (o.Type === "Bulk" && o.BulkClass) { effect.push({ kind: "poolDelta", klass: o.BulkClass, dQty: -qty }); continue; }
    if (!o["Lot ID"]) continue;
    const isSealed = !!DB.table("sealed").find(r => r["Lot ID"] === o["Lot ID"]);
    if (isSealed) effect.push({ kind: "lotDelta", table: "sealed", lotId: o["Lot ID"], dQty: -qty, dFMV: 0 });
    else effect.push({ kind: "lotDelta", table: "cards", lotId: o["Lot ID"], dQty: -qty, dFMV: 0, statusBefore: "Available", statusAfter: "Traded" });
  }
  const inc: any[] = Array.isArray(trade?.Incoming) ? trade.Incoming : [];
  for (const i of inc) {
    const qty = Number(i.Qty) || 1;
    if (i.AsType === "Bulk" && i.BulkClass) { effect.push({ kind: "poolDelta", klass: i.BulkClass, dQty: qty }); continue; }
    if (!i["Lot ID"]) continue;
    const table: "cards" | "sealed" = i.AsType === "Sealed" ? "sealed" : "cards";
    effect.push({ kind: "lotCreate", table, lotId: i["Lot ID"] });
  }
  return effect;
}

/**
 * Reconstruct a rip's inventory effect from its own data, for rips saved before
 * effects were recorded. The sealed box decrement becomes a lotDelta; each
 * pulled card (anything sourced from this rip) becomes a lotCreate; each bulk
 * pull becomes a poolDelta. FMV deltas are 0 (refreshable).
 */
export function reconstructRipEffect(rip: any): InvEffect {
  const effect: InvEffect = [];
  const ripId = rip?.["Rip ID"];
  const qtyOpened = Number(rip?.["Qty Opened"]) || 0;
  if (rip?.["Sealed Lot ID"] && qtyOpened > 0) {
    effect.push({ kind: "lotDelta", table: "sealed", lotId: rip["Sealed Lot ID"], dQty: -qtyOpened, dFMV: 0 });
  }
  if (ripId) {
    for (const c of DB.table("cards")) {
      if (c["Source ID"] === ripId) effect.push({ kind: "lotCreate", table: "cards", lotId: c["Card Lot ID"] });
    }
  }
  const bulk: any[] = Array.isArray(rip?.["Bulk Pulls"]) ? rip["Bulk Pulls"] : [];
  for (const b of bulk) {
    const qty = Number(b["Qty"]) || 0;
    if (qty > 0 && b["Class"]) effect.push({ kind: "poolDelta", klass: b["Class"] as BulkClass, dQty: qty });
  }
  return effect;
}

/**
 * Reconstruct a grading submission's effect from its own data, for gradings
 * saved before effects were recorded. Best-effort: we can't recover the exact
 * pre-grading grade / class / FMV (they weren't snapshotted), so reverse
 * restores Status to "Available" and backs out the capitalized service fee +
 * shipping share from basis. New gradings store a full before-snapshot instead.
 */
export function reconstructGradingEffect(grading: any): InvEffect {
  const effect: InvEffect = [];
  const cards: any[] = Array.isArray(grading?.Cards) ? grading.Cards : [];
  const shipPerCard = cards.length > 0 ? +((Number(grading?.["Total Shipping"]) || 0) / cards.length).toFixed(2) : 0;
  const graded = ["Returned", "Listed", "Sold"].includes(grading?.["Status"]);
  for (const c of cards) {
    const lotId = c["Card Lot ID"];
    if (!lotId) continue;
    const src = DB.table("cards").find(r => r["Card Lot ID"] === lotId);
    if (!src) continue;
    const qty = Number(src["Qty"]) || 1;
    const before: Record<string, any> = { "Status": "Available" };
    if (graded) {
      const addition = +((Number(c["Service Fee"]) || 0) + shipPerCard).toFixed(2);
      const restoredTotalBasis = +((Number(src["Total Basis"]) || 0) - addition).toFixed(2);
      before["Total Basis"] = restoredTotalBasis;
      before["Unit Basis"] = qty > 0 ? +(restoredTotalBasis / qty).toFixed(2) : 0;
      before["Unrealized G/L"] = +((Number(src["Current FMV"]) || 0) - restoredTotalBasis).toFixed(2);
    }
    effect.push({ kind: "lotFieldChange", table: "cards", lotId, before });
  }
  return effect;
}

/** Stored effect if present, else reconstruct from the row's own data. */
export function effectForTransaction(table: TableKey, row: any): InvEffect {
  const stored = row?.["Inv Effect"];
  if (Array.isArray(stored) && stored.length) return stored as InvEffect;
  switch (table) {
    case "sales":   return reconstructSaleEffect(row);
    case "trades":  return reconstructTradeEffect(row);
    case "rips":    return reconstructRipEffect(row);
    case "grading": return reconstructGradingEffect(row);
    default: return [];
  }
}

/**
 * Reverse a transaction's inventory footprint (used before delete). Returns
 * the guard result so callers can abort a blocked delete with the reason.
 */
export function reverseTransaction(table: TableKey, id: string, idField: string): ReverseResult {
  const row = DB.table(table).find(r => r[idField] === id);
  if (!row) return { ok: true };
  const effect = effectForTransaction(table, row);
  if (!effect.length) return { ok: true };
  return reverseEffect(effect, { excludeId: id });
}
